import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TreetopPropertiesProvider } from 'shared/util-common';

import { UserInitResponse, UserDto, User, GuidelineReviewInfo, TableOfContentNodeDto } from '@treetop/shared/model';

import { mapUserDtoToUser } from './user.http.service.util';
import { GuidelineSyncInfo } from '../../../../model/src/lib/user.model';

export type GuidelineSyncInfoDto = {
  uniqueId?: string;
  version?: string;
};

@Injectable({
  providedIn: 'root',
})
export class UserHttpService {
  private http = inject(HttpClient);
  private baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  public trackLogin(): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/users/track-login`, null);
  }

  public setHomeOrganisation(organisationId: string): Observable<User> {
    const params = new HttpParams().set('organisationId', organisationId);
    return this.http.put<UserDto>(`${this.baseUrl}/users/home-organization`, null, { params }).pipe(map(mapUserDtoToUser));
  }

  public setCurrentOrganisation(organisationId: string): Observable<User> {
    const params = new HttpParams().set('organisationId', organisationId);
    return this.http.put<UserDto>(`${this.baseUrl}/users/current-organization`, null, { params }).pipe(map(mapUserDtoToUser));
  }

  public setRootOrganisation(organisationId: string): Observable<User> {
    const params = new HttpParams().set('organizationId', organisationId);
    return this.http.put<UserDto>(`${this.baseUrl}/users/root-organization`, null, { params }).pipe(map(mapUserDtoToUser));
  }

  public initUser(idToken: string): Observable<User> {
    return this.http.post<UserInitResponse>(`${this.baseUrl}/users/init`, { idToken }).pipe(
      map((response: UserInitResponse) => {
        if (response.success === true) {
          return mapUserDtoToUser(response.user);
        }

        throw new Error(response.message);
      }),
    );
  }

  public logoutUser(): Observable<string> {
    return this.http.post<string>(`${this.baseUrl}/authentication/invalidate-token`, null);
  }

  public getTableOfContent(): Observable<TableOfContentNodeDto> {
    return this.http.get<TableOfContentNodeDto>(`${this.baseUrl}/users/table-of-content`);
  }

  public getGuidelinesToReview(): Observable<GuidelineReviewInfo[]> {
    return this.http.get<GuidelineReviewInfo[]>(`${this.baseUrl}/users/guidelines-to-review`);
  }

  public getGuidelinesToSync(): Observable<GuidelineSyncInfo[]> {
    return this.http.get<GuidelineSyncInfoDto[]>(`${this.baseUrl}/users/guidelines-to-sync`);
  }

  public loadAllUsers(): Observable<User[]> {
    return this.http
      .get<UserDto[]>(`${this.baseUrl}/users`)
      .pipe(map((users: UserDto[]) => users.map((userDto) => mapUserDtoToUser(userDto))));
  }

  public loadUsersForGuidelineAuthors(): Observable<User[]> {
    return this.http
      .get<UserDto[]>(`${this.baseUrl}/users/users-for-guideline-authors`)
      .pipe(map((users: UserDto[]) => users.map((userDto) => mapUserDtoToUser(userDto))));
  }

  public loadUsersForGuidelineOwners(): Observable<User[]> {
    return this.http
      .get<UserDto[]>(`${this.baseUrl}/users/users-for-guideline-owners`)
      .pipe(map((users: UserDto[]) => users.map((userDto) => mapUserDtoToUser(userDto))));
  }
}
